import { Trans } from '@lingui/react';
import { AddToCart, ProductPrice } from '@we-make-websites/ui-lib';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import { useContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import { CartContext } from 'components/cartContext/CartContext';
import { useFeatureFlags } from 'components/featureFlagsProvider/FeatureFlagsContext';
import { OverlayContext } from 'components/overlay/OverlayContext';
import { ProductContext } from 'components/productContext/ProductProvider';
import { ProductQuantitySelectorWithState } from 'components/productQuantitySelector/ProductQuantitySelector';
import { ProductTitle } from 'components/productTitle/ProductTitle';
import { ProductType } from 'components/productType/ProductType';
import { ProductVariantSelector } from 'components/productVariantSelector/ProductVariantSelector';
import styles from './StickyProductForm.module.scss';

type ProductFormInput = {
  quantity: number;
};

export const StickyProductForm = ({ visible }: { visible: boolean }) => {
  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);

  const productContext = useContext(ProductContext);
  if (productContext === undefined)
    throw new Error('StickyProductForm must be used within a ProductContext');

  const cartContext = useContext(CartContext);
  if (cartContext === undefined)
    throw new Error('StickyProductForm must be used within a CartContext');

  const { featureFlags } = useFeatureFlags();

  const {
    amazonCart,
    backInStock,
    cartEnabled,
    showPrice: showProductPrice,
    mikmakEnabled,
  } = featureFlags[locale];
  const formRef = useRef<HTMLFormElement>(null);
  const { open } = useContext(OverlayContext);

  const {
    title,
    price,
    selectedVariant,
    isLoading,
    proCustomer,
    proOnly,
    variants,
    selectVariant,
    productType,
    colorSystems,
    handle,
    ultaProductUrl,
    customBuyNowUrl,
    asin,
    collections,
    recommendedEssentials,
  } = productContext;

  const toggleUpSells = !!recommendedEssentials.length;

  /**
   * Is current selected variant available for sale
   */
  const availableForSale = selectedVariant?.availableForSale;

  /**
   * If product or selected variant is pro only and logged in customer is not a pro
   */
  const proOnlyHide = (selectedVariant?.proOnly || proOnly) && !proCustomer;
  /**
   * Used to identify enabled AMAZON cart and pro customers.
   */
  const proAmazonCart = amazonCart && proCustomer;
  /**
   * Used to check if AMAZON cart is enabled and consumer customer
   * and product do not have ASIN data populated in metafield.
   */
  const consumerAmazonCartNoProductASIN = amazonCart && !proCustomer && !asin;
  /**
   * A flag to decide if the form should be disabled or not.
   */
  const disableForm =
    proOnlyHide ||
    ultaProductUrl ||
    customBuyNowUrl ||
    proAmazonCart ||
    consumerAmazonCartNoProductASIN ||
    !availableForSale ||
    !cartEnabled;

  /**
   * Only show price if product has a price and pro only hide is false, and global feature flag is enabled.
   */
  const showPrice = price && !proOnlyHide && showProductPrice;

  const { cartLineAdd, cartLoading, cartErrors } = cartContext;

  const addToCart = (quantity: number) => {
    if (!selectedVariant) return;

    cartLineAdd({ id: selectedVariant.id, quantity }, toggleUpSells);
  };

  const { handleSubmit, control } = useForm<ProductFormInput>();

  const onSubmit: SubmitHandler<ProductFormInput> = (data) => {
    if (!availableForSale && backInStock) {
      open({
        ignoreDismissed: true,
        namespace: 'backInStock',
      });

      return;
    }

    if (disableForm) return;

    addToCart(data.quantity);
  };

  const activeColorSystem = colorSystems.find(
    (colorSystem) => colorSystem.handle === handle
  );
  const activeColorSystemUrl = activeColorSystem?.swatchUrl || '';

  return (
    <div
      className={clsx(
        styles.stickyProductForm,
        visible && styles['stickyProductForm--visible']
      )}
    >
      <div className={clsx(styles.stickyProductForm__content, 'container')}>
        <div className={styles.stickyProductForm__details}>
          {activeColorSystemUrl && (
            <Image
              className={styles.stickyProductForm__color}
              src={activeColorSystemUrl}
              width={25}
              height={25}
              alt=""
            />
          )}
          <div>
            <ProductType
              className={clsx(
                'text-utility-utility-small-mobile text-utility-utility-small-desktop',
                styles.stickyProductForm__productType
              )}
              type={productType}
              collections={collections}
            />
            <ProductTitle
              {...{ title, isLoading, isSticky: true }}
              className={[
                styles.stickyProductForm__productTitle,
                'productTitle',
              ].join(' ')}
            />

            {showPrice && (
              <div
                className={styles['stickyProductForm__productPrice--wrapper']}
              >
                <ProductPrice
                  {...{
                    price: selectedVariant?.price || price,
                    isLoading,
                  }}
                  type="unit"
                  className={clsx(
                    styles.stickyProductForm__productPrice,
                    'text-heading-h7-desktop text-body-1-bold-mobile',
                    selectedVariant?.compareAtPrice &&
                      styles['stickyProductForm__productPrice--sale']
                  )}
                />
                {selectedVariant?.compareAtPrice && (
                  <ProductPrice
                    {...{
                      price: selectedVariant?.compareAtPrice,
                      isLoading,
                    }}
                    type="compare"
                    className={clsx(
                      styles.stickyProductForm__productPrice,
                      'text-heading-h7-desktop text-body-1-bold-mobile'
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <form
          className={styles.stickyProductForm__form}
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
        >
          <fieldset
            disabled={cartLoading}
            className={styles.stickyProductForm__fieldset}
            data-form-errored={!!cartErrors}
            data-test-id="productFormFieldset"
          >
            <legend className="visually-hidden">
              <Trans id="product.sticky.form.legend">Product Form</Trans>
            </legend>
            {variants.length > 1 && (
              <div className={styles.stickyProductForm__variantSelector}>
                <ProductVariantSelector
                  {...{
                    variants,
                    selectedVariant,
                    selectVariant,
                    proCustomer,
                    isLoading,
                  }}
                  namespace="stickyProductForm"
                />
              </div>
            )}

            <div
              className={clsx(
                styles.stickyProductForm__addToCartRow,
                disableForm && styles['stickyProductForm__addToCartRow--full']
              )}
            >
              {!disableForm && (
                <Controller
                  control={control}
                  name="quantity"
                  render={({ field }) => (
                    <ProductQuantitySelectorWithState
                      {...field}
                      className={styles.productForm__quantity}
                      location="product"
                    />
                  )}
                />
              )}
              <AddToCart
                product={{
                  ...productContext,
                  proOnly: selectedVariant?.proOnly || proOnly,
                  availableForSale,
                  barcode: selectedVariant?.barcode,
                }}
                amazonCart={amazonCart}
                backInStock={backInStock}
                proCustomer={proCustomer}
                cartEnabled={cartEnabled}
                mikmakEnabled={mikmakEnabled}
              />
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
};
